import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Zap, Shield, Unlock, Headphones } from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { ABOUT_PAGE } from "@/lib/extendedContent";
import { PARTNERS } from "@/lib/i18n";

const VALUE_ICONS = [Zap, Shield, Unlock, Headphones];

export default function About() {
  const { lang, t } = useI18n();
  const p = pickLang(ABOUT_PAGE, lang);

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="overline">{p.overline}</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] max-w-5xl text-white">
            {p.title_a} <span className="gradient-text">{p.title_b}</span>
          </h1>
          <p className="mt-8 text-base md:text-xl text-slate-200 max-w-3xl leading-relaxed">{p.subtitle}</p>

          {/* Stats */}
          <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {p.stats.map((s, i) => (
              <div key={i} className="glass rounded-2xl p-5 md:p-6" data-testid={`about-stat-${i}`}>
                <div className="font-display text-3xl md:text-4xl font-bold gradient-text">{s.value}</div>
                <div className="mt-2 text-[10px] md:text-xs font-mono uppercase tracking-widest text-slate-300">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="py-20 md:py-28 border-t border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-8 md:gap-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="glass rounded-3xl p-8 md:p-10" data-testid="about-mission">
            <div className="overline">{p.mission_overline}</div>
            <h2 className="font-display mt-5 text-3xl md:text-4xl font-bold tracking-tighter text-white leading-[1.05]">{p.mission_title}</h2>
            <p className="mt-6 text-slate-200 leading-relaxed">{p.mission_body}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.08 }} className="glass rounded-3xl p-8 md:p-10" data-testid="about-vision">
            <div className="overline">{p.vision_overline}</div>
            <h2 className="font-display mt-5 text-3xl md:text-4xl font-bold tracking-tighter text-white leading-[1.05]">{p.vision_title}</h2>
            <p className="mt-6 text-slate-200 leading-relaxed">{p.vision_body}</p>
          </motion.div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12 md:mb-16">
            <div className="overline">{p.timeline_overline}</div>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05] max-w-3xl">
              {p.timeline_title_a} <span className="gradient-text">{p.timeline_title_b}</span>
            </h2>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#1FE0A8] via-[#1FA8FF] to-transparent opacity-50" />

            <div className="space-y-10 md:space-y-14">
              {p.timeline.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5 }}
                  className="relative grid md:grid-cols-2 gap-4 md:gap-12 items-center"
                  data-testid={`about-timeline-${i}`}
                >
                  {/* Dot */}
                  <div className="hidden md:block absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full gradient-bg shadow-[0_0_20px_rgba(31,168,255,0.6)] z-10" />

                  <div className={`${i % 2 === 0 ? "md:text-right md:pr-12" : "md:order-2 md:pl-12"}`}>
                    <div className="font-display text-4xl md:text-5xl font-bold gradient-text">{item.year}</div>
                  </div>
                  <div className={`glass rounded-2xl p-6 md:p-7 ${i % 2 === 0 ? "md:order-2 md:ml-12" : "md:mr-12"}`}>
                    <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-white">{item.title}</h3>
                    <p className="mt-3 text-slate-200 text-sm md:text-base leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 md:py-28 border-t border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <div className="overline">{p.values_overline}</div>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">{p.values_title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {p.values.map((v, i) => {
              const Icon = VALUE_ICONS[i] || Zap;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="glass rounded-2xl p-7 md:p-8 card-hover"
                  data-testid={`about-value-${i}`}
                >
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center text-black shrink-0">
                      <Icon size={22} strokeWidth={2.2} />
                    </div>
                    <div>
                      <h3 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">{v.title}</h3>
                      <p className="mt-3 text-slate-200 text-sm md:text-base leading-relaxed">{v.desc}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="overline justify-center inline-flex">{p.leadership_overline}</div>
          <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">{p.leadership_title}</h2>
          <p className="mt-6 text-slate-200 max-w-2xl mx-auto">{p.leadership_sub}</p>
          <div className="mt-10">
            <Link to="/contact" data-testid="about-leadership-cta" className="btn-primary">{t("cta.contact_sales")} <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-16 md:py-20 border-y border-white/10 bg-[#324567]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8 text-center">
          <div className="overline justify-center inline-flex">{lang === "fr" ? "Partenaires" : lang === "es" ? "Partners" : lang === "pt" ? "Parceiros" : "Partners"}</div>
        </div>
        <div className="marquee-fade">
          <Marquee gradient={false} speed={30} pauseOnHover>
            {PARTNERS.concat(PARTNERS).map((pp, i) => (
              <div key={i} className="mx-12 font-display text-2xl md:text-3xl text-slate-400 hover:text-white transition-colors tracking-tight">
                {pp}
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
            {p.cta_title_a} <span className="gradient-text">{p.cta_title_b}</span>
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="about-cta-demo" className="btn-primary">{t("cta.demo_short")} <ArrowRight size={18} /></Link>
            <Link to="/contact" data-testid="about-cta-contact" className="btn-ghost">{t("cta.contact_sales")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
